import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminFeaturedButtonsEditor() {
    const [buttons, setButtons] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchButtons = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/buttons", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setButtons(res.data.content || []);
            } catch (error) {
                console.error("Error fetching buttons:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchButtons();
    }, []);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/content", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setPages(res.data);
            } catch (error) {
                console.error("Error fetching pages:", error);
            }
        };
        
        fetchPages();
    }, []);

    const handleChange = (index, field, value) => {
        const updated = [...buttons];
        updated[index][field] = value;
        setButtons(updated);
    };

    const moveButton = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= buttons.length) return;

        const updated = [...buttons];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setButtons(updated);
        setOpenIndex(newIndex);
    };

    const saveButtons = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/buttons", { content: buttons }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Buttons saved!");
        } catch {
            toast.error("Failed to save buttons");
        } finally {
            setSaving(false);
        }
    };

    const addButton = () => {
        const newButton = {
            label: "",
            link: "",
            color: "#c80851"
        };
        setButtons([newButton, ...buttons]);
        setOpenIndex(0);
    };

    const deleteButton = async (index) => {
        const button = buttons[index];
        const ok = await confirmSwal({
            title: `Delete button ${button.name || "(No Name)"}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = buttons.filter((_, i) => i !== index);
        setButtons(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/buttons", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Button deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    if (loading) return <p>Loading buttons...</p>;

    return (
        <div>
            <h4>Featured Buttons</h4>
            <p className="text-body-secondary">Buttons are featured right below the header slideshow on the homepage.</p>

            <button className="btn btn-admin mb-3" onClick={addButton}>+ Add Button</button>

            <div className="accordion" id="projectsAccordion">
                {buttons.map((button, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`heading-${index}`}>
                            <button
                                className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(index)}
                            >
                                <strong>{button.label || "(No Label)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Label</label>
                                    <input className="form-control" value={button.label} onChange={(e) => handleChange(index, "label", e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Link</label>
                                    <select
                                        className="form-select"
                                        value={button.link}
                                        onChange={(e) => handleChange(index, "link", e.target.value)}
                                    >
                                        <option value="">(No link)</option>
                                        {pages.map((p) => (
                                            <option key={p.slug} value={p.slug}>
                                                {p.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Color</label>
                                    <input 
                                        type="color" 
                                        className="form-control form-control-color" 
                                        value={button.color} 
                                        onChange={(e) => handleChange(index, "color", e.target.value)} 
                                        style={{ width: "80px", height: "20px" }}
                                    />
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveButton(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveButton(index, 1)}
                                            disabled={index === buttons.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteButton(index)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveButtons} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
